import { UrlUtils } from "Global/Base/Utils/UrlUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { PopupMenu } from "../PopupMenu";
import { ControlParams, IEditableControl, IRequiresActualObject } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export class LinkEdit extends EditableControl implements IEditableControl, IRequiresActualObject {
    //#region Fields
    public override innerElement: HTMLInputElement;

    private _innerInputElement: HTMLInputElement;
    private _innerLinkElement: HTMLAnchorElement;
    private _maxLength: number;

    private static _prependProtocol: string = "http"; // protokol, ktery bude automaticky pripojen pokud neni v adrese
    private _hrefValue: string;

    private _actualObject: OWObject;

    //Listeners
    private _listenerFocus: EventListener;
    private _listenerBlur: EventListener;
    private _listenerClick: EventListener;
    private _listenerChange: EventListener;
    private _listenerDblclick: EventListener;
    private _listenerPaste: EventListener;
    //#endregion Fields

    constructor(params: ControlParams) {
        super(params);

        this.element.classList.add("Edit");
        this.element.classList.add("LinkEdit");
        this.element.classList.remove("EditableControl");
        this._maxLength = this.getNumDataAttr("maxLength");

        this._createElements();
        this.setListeners();
    }
    //#endregion Constructors

    //#region Properties
    public set actualObject(value: OWObject) {
        if (value != null) {
            try {
                this._actualObject = value;
                //this.ActualObject.ClassInfo.GetAttributeInfo(this.AttrName).then((res) => { logger.log(res) });
                //logger.log(this.ActualObject);
            } catch (exp) {
                alert(exp + "\n" + "LinkEdit.ts" + " - " + "set ActualObject");
            }
        } else {
            this._clearActualObject();
        }
    }

    public get actualObject(): OWObject {
        return this._actualObject;
    }

    private _clearActualObject(): void {
        this._actualObject = null;
        this._innerInputElement.value = "";
        this._innerLinkElement.textContent = "";
    }

    public override get controlValue(): string {
        return this._innerInputElement.value;
    }

    /**
     * Nastavi hodnotu editu
     */
    public override set controlValue(value: string) {
        this._setGuiValid();
        const el = this._innerInputElement;

        if (this._maxLength && value && value.length > this._maxLength) {
            el.value = value.substr(0, this._maxLength);
            this._alertOnLength();
        } else {
            el.value = value;
        }
        this._innerLinkElement.textContent = el.value;

        if (this.isEditing)
            this.validate();

        this._setHrefValue(el);

        if (this._hrefValue.length > 0) {
            this._innerLinkElement.setAttribute("href", this._hrefValue);
            this._innerLinkElement.setAttribute("target", "_blank");
        } else {
            this._innerLinkElement.removeAttribute("href");
            this._innerLinkElement.removeAttribute("target");
        }
    }
    //#endregion Properties

    //#region Methods

    private _setHrefValue(element: HTMLInputElement): boolean {
        let validate_result = true;

        if (UrlUtils.isUrl(element.value)) {
            this._hrefValue = element.value;
            this._innerInputElement.type = "url";
            validate_result = true;
        } else if (UrlUtils.isEmail(element.value.replace("mailto://", ""))) {
            this._hrefValue = "mailto://" + element.value;
            this._innerInputElement.type = "email";
            validate_result = true;
        } else if (UrlUtils.isUrl(LinkEdit._prependProtocol + "://" + element.value)) {
            this._hrefValue = LinkEdit._prependProtocol + "://" + element.value;
            this._innerInputElement.type = "text";
            validate_result = true;
        } else if (UrlUtils.isFile(element.value.replace("file:///", ""))) {
            this._hrefValue = "file:///" + element.value;
        } else {
            this._hrefValue = "";
            validate_result = false;
        }

        return validate_result;
    }

    /** Vytvori samotny input */
    private _createElements(): void {
        const input = createElement({ type: "input", attrs: { "type": "url" } }) as HTMLInputElement;

        if (this._placeholderLocKey) {
            Localization.addLocTxtData(input, this._placeholderLocKey, "placeholder");
        }

        const link = document.createElement("a");

        if (this._maxLength) {
            input.maxLength = this._maxLength;
        }

        this.innerElement = input;
        this._innerInputElement = input;
        this._innerLinkElement = link;

        this.element.appendChild(this._innerInputElement);
        this.element.appendChild(this._innerLinkElement);
    }

    public setListeners(): void {
        this.isEditingChanged.add((_s: IEditableControl, obj: any) => {
            if (obj.value)
                this.element.classList.add("EditableControl");
            else
                this.element.classList.remove("EditableControl");
        });

        this._innerInputElement.addEventListener("focus", this._listenerFocus = (e: FocusEvent) => {
            this.getFocus.trigger(this, e);
            if (this.enabled)
                this.element.classList.add("focused");
        });
        this._innerInputElement.addEventListener("blur", this._listenerBlur = (e: FocusEvent) => {
            this.lostFocus.trigger(this, e);
            this.element.classList.remove("focused");
        });
        this._innerLinkElement.addEventListener("click", (e) => EventHelper.stopPropagation(e));

        this._innerInputElement.addEventListener("change", this._listenerChange = () => {
            //this.Validate();
            this.controlValue = this._innerInputElement.value;
            //if (this.Validate()) {
            this.valueChanged.trigger(this, { Sender: this });
            //}
        });

        if (this._maxLength) {
            // vyhazovani hlasky pri preteceni pri paste
            this._innerInputElement.addEventListener("paste", this._listenerPaste = (e: Event) => {
                const ev: ClipboardEvent = e as ClipboardEvent;
                if (ev.clipboardData) {
                    const newDataLen = ev.clipboardData.getData("text/plain").length;
                    if (newDataLen + this._innerInputElement.value.length > this._maxLength) {
                        self.setTimeout(() => {
                            this._alertOnLength();
                        });
                    }
                }
            });
        }
    }

    private _alertOnLength(): void {
        if (!this._errorPopup)
            this._errorPopup = new PopupMenu(this._errorElement, false);

        if (this._errorElement.textContent.indexOf(":") == 0) {
            const spanErr = document.createElement("span"); //kvuli lokalizaci je pred cislo maximalni delky retezce pridan element s lok.popiskem
            Localization.addLocData(spanErr, "GUI-452712");
            this._errorElement.appendChild(spanErr);
            this._errorElement.textContent = this._maxLength + "";
        } else {
            const locText = this._errorElement.textContent.split(":");
            this._errorElement.textContent = locText + ": " + this._maxLength;
        }
        this._errorElement.style.display = "block";
        this._errorPopup.setVisibility(true);

        self.setTimeout(() => {
            if (this.validate()) {
                this._errorElement.style.display = "none";
                this._errorPopup.setVisibility(false);
            }
        }, 5000);
    }

    //destruktor
    public override dispose(): void {
        this._innerInputElement.removeEventListener("focus", this._listenerFocus);
        this._innerInputElement.removeEventListener("blur", this._listenerBlur);
        this._innerInputElement.removeEventListener("click", this._listenerClick);
        this._innerInputElement.removeEventListener("change", this._listenerChange);
        this._innerInputElement.removeEventListener("dblclick", this._listenerDblclick);
        this._innerInputElement.removeEventListener("paste", this._listenerPaste);

        this._actualObject = null;

        super.dispose();
    }

    //#endregion Methods
}
